import { Close as CloseIcon, Lock as LockIcon } from "@mui/icons-material";
import {
  Stack,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DefaultLoader from "components/common/DefaultLoader";
import InlineSearch from "components/common/InlineSearch";
import Options from "components/common/Options";
import _ from "lodash";
import { useMemo, useState, useEffect } from "react";
import * as claimsService from "services/claimsService";
import * as staticDataService from "services/staticDataService";

const IndustryClassExposureLinkerDialog = ({
  open,
  close,
  selectedIndustryClass,
}) => {
  const theme = useTheme();
  const {
    data: defaultMeasures,
    refetch: refetchDefaultMeasures,
    isLoading: defaultMeasuresLoading,
  } = claimsService.useDefaultMeasuresByIndustryClassQuery();

  const {
    data: retrievedExposures = [],
    isLoading: exposuresLoading,
  } = claimsService.useExposuresQuery();

  const [
    triggerUpdateConfig,
  ] = staticDataService.useAdminUpdateConfigMutation();

  const [isInitialized, setIsInitialized] = useState(false);
  const isLoading =
    defaultMeasuresLoading || exposuresLoading || !isInitialized;
  const [searchExposure, setSearchExposure] = useState("");
  const [selectedExposures, setSelectedExposures] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedIndustryClass) {
      setSelectedExposures(
        defaultMeasures?.[selectedIndustryClass?.key] ||
          defaultMeasures?.["ALL"] ||
          []
      );
      setIsInitialized(true);
    }
  }, [selectedIndustryClass, defaultMeasures]);

  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);

  const filteredExposures = useMemo(() => {
    if (!retrievedExposures) return [];
    return retrievedExposures.filter((exposure) =>
      exposure.name.toLowerCase().includes(searchExposure.toLowerCase())
    );
  }, [retrievedExposures, searchExposure]);

  const lockedExposureKeys = useMemo(() => {
    return defaultMeasures?.["ALL"] || [];
  }, [defaultMeasures]);

  const exposureItems = useMemo(() => {
    return filteredExposures.map((exposure) => {
      const isLocked = lockedExposureKeys.includes(exposure.key);
      return {
        ...exposure,
        id: exposure.key,
        label: exposure.name,
        selected: selectedExposures.some((e) => e === exposure.key) || isLocked,
        locked: isLocked,
        tip: isLocked
          ? "This exposure is set as default for all industry classes and cannot be changed here"
          : undefined,
      };
    });
  }, [filteredExposures, selectedExposures, lockedExposureKeys]);

  const handleToggleExposure = (exposure) => {
    if (exposure.locked) return;

    setSelectedExposures((prev) => {
      const exists = prev.some((e) => e === exposure.key);
      if (exists) {
        return prev.filter((e) => e !== exposure.key);
      } else {
        return [...prev, exposure.key];
      }
    });
  };

  const hasChanges = useMemo(() => {
    if (!selectedIndustryClass) return false;
    const currentDefaultMeasures =
      defaultMeasures?.[selectedIndustryClass.key] || [];
    return !_.isEqual(
      [...currentDefaultMeasures].sort(),
      [...selectedExposures].sort()
    );
  }, [selectedIndustryClass, defaultMeasures, selectedExposures]);

  const handleSave = async () => {
    if (!selectedIndustryClass || !hasChanges) return;

    setIsSaving(true);
    setError(null);

    try {
      await triggerUpdateConfig({
        key: "exposures",
        value: {
          defaultMeasuresByIndustryClass: {
            ...defaultMeasures,
            [selectedIndustryClass.key]: selectedExposures,
          },
        },
      });

      await refetchDefaultMeasures();

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(
        "An error has occured when saving, please contact support at: support@cactus.bm"
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedIndustryClass) {
    return null;
  }

  return (
    <Dialog open={open} onClose={close} maxWidth={"md"} fullWidth>
      <DialogTitle>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography variant={"h6"}>
            {`Default Exposures for ${selectedIndustryClass.name}`}
          </Typography>
          <IconButton onClick={close} edge={"end"}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: theme.brand.white }}>
        {isLoading ? (
          <Box display={"flex"} justifyContent={"center"} p={4}>
            <DefaultLoader color={theme.brand.white} />
          </Box>
        ) : error ? (
          <Alert severity={"error"} sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Stack spacing={2}>
            <InlineSearch
              placeholder={"Search exposures..."}
              onChange={(text) => setSearchExposure(text.toLowerCase())}
              fullWidth
            />

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 2,
                minHeight: 300,
                maxHeight: 400,
                overflow: "auto",
              }}
            >
              {exposureItems.length > 0 ? (
                <>
                  <Typography
                    variant={"caption"}
                    display={"block"}
                    color={"textSecondary"}
                    gutterBottom={true}
                  >
                    {`${selectedExposures.length} of ${retrievedExposures.length} exposures selected`}
                  </Typography>
                  <Options
                    items={exposureItems.map((item) => ({
                      ...item,
                      className: item.locked ? "locked" : "",
                      label: (
                        <Box
                          component={"span"}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {item.name}
                          {item.locked && <LockIcon fontSize={"small"} />}
                        </Box>
                      ),
                    }))}
                    onClick={handleToggleExposure}
                  />
                </>
              ) : (
                <Typography
                  color={"textSecondary"}
                  textAlign={"center"}
                  sx={{ mt: 2 }}
                >
                  {"No exposures found"}
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {saved && (
          <Alert severity={"success"} sx={{ my: 0, flex: 1 }}>
            {"Changes saved successfully!"}
          </Alert>
        )}
        <Box sx={{ display: "flex", flex: 1, justifyContent: "flex-end" }}>
          <Button
            onClick={handleSave}
            variant={"contained"}
            color={"primary"}
            disabled={isSaving || !hasChanges || !isInitialized}
          >
            {isSaving ? <DefaultLoader color={theme.brand.white} /> : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default IndustryClassExposureLinkerDialog;
